import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Card, CardBody, Button } from "reactstrap";
import { useHistory } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";

import { BreadCrumb, BreadCrumbSelect } from "../../../../components/Common";
import {
  LocalizedProperty,
  PaginateQuery,
  State,
  StudentDto,
  SubjectDto,
} from "../../../../interfaces";
import {
  getCourseAndGradesSelectList,
  getStudents,
  resetCitySelectList,
  resetGradeSelectListByCourseLocal,
  resetSchoolSelectList,
  toggleBlockUser,
  toggleBlockUserConsent,
} from "../../../../store/actions";
import { Paginated } from "../../../../models";
import {
  ActionButton,
  AppTable,
  CitySelect,
  ColumnHeader,
  CourseSelect,
  DateColumn,
  GovernorateSelect,
  GradeSelect,
  HTMLColumn,
  SchoolSelect,
  TableSpinner,
  TextColumn,
} from "../../../../components";
import { ActionTypeEnum } from "../../../../enums";
import { ManualPurchaseSubjectModal, StudentsSubjectsModal } from ".";
import ConfirmationModal from "../../../../components/Common/ConfirmationModal";
import { SendPushNotificationModal } from "../../../NotifyUsers";

type TProps = {};
export const StudentUsersPage: React.FC<TProps> = () => {
  //#region hooks

  const history = useHistory();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  //#endregion

  //#region redux state

  const { loading, response } = useSelector(
    (state: any): State<Paginated<StudentDto>> => state.students.students
  );
  const {
    loading: toggleBlockUserLoading,
    success: toggleBlockUserSuccess,
    error: toggleBlockUserError,
  } = useSelector((state: any): State<any> => state.user.toggleBlockUser);

  useSelector(
    (state: any): State<SubjectDto[]> => state.subject.subjectsByGrade
  );
  //#endregion

  //#region internal state
  const initState = new PaginateQuery();

  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  const [showBlockUnblockModal, setShowBlockUnblockModal] = useState(false);
  const [showSubjectsModal, setShowSubjectModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<StudentDto | null>();
  const [filterGovernorateId, setFilterGovernorateId] = useState<string>("");
  const [filterCityId, setFilterCityId] = useState<string>("");
  const [filterSchoolId, setFilterSchoolId] = useState<string>("");
  const [filterCourseId, setFilterCourseId] = useState<string>("");
  const [filterGradeId, setFilterGradeId] = useState<string>("");
  const [filterGenderId, setFilterGenderId] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<StudentDto[]>([]);
  const [toggledClearRows, setToggleClearRows] = React.useState(false);
  const [showSendPushNotificationModal, setShowSendPushNotificationModal] =
    useState(false);
  const [showAssignSubjectManuallyModal, setShowAssignSubjectManuallyModal] =
    useState(false);

  //#endregion

  document.title = `${t("documentTitle", { pageName: "students" })}`;

  const getSubscriptions = (
    subscriptions: {
      name: LocalizedProperty;
      count: number;
    }[]
  ) => {
    if (i18n.language === "ar") {
      var table = `<table>`;
      subscriptions.forEach(
        (item: { name: LocalizedProperty; count: number }) => {
          table += `<tr>
              <td style="padding:5px;font-weight:500">${item.name?.en}</td>
              <td style="padding:5px;padding:5px;font-weight:500">${item.count}</td> 
            </tr>`;
        }
      );
      table += `</table>`;
      return table;
    } else {
      var table = `<table>`;
      subscriptions.forEach(
        (item: { name: LocalizedProperty; count: number }) => {
          table += `<tr>
              <td style="padding:5px;font-weight:500">${item.name?.en}</td>
              <td style="padding:5px;padding:5px;font-weight:500">${item.count}</td> 
            </tr>`;
        }
      );
      table += `</table>`;
      return table;
    }
  };

  const columns: TableColumn<StudentDto>[] = [
    {
      name: <ColumnHeader title="name" />,
      cell: (row) => (
        <TextColumn text={row.name[i18n.language === "ar" ? "ar" : "en"]} />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="createdAt" />,
      cell: (row) => <DateColumn date={row.createdAt} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="phoneNumber" />,
      cell: (row) => (
        <a
          style={{ cursor: "pointer" }}
          href={`tel:+${row.phoneNumber?.countryCode}${row.phoneNumber?.phoneNumber}`}
        >
          <u>{`+${row.phoneNumber?.countryCode}${row.phoneNumber?.phoneNumber}`}</u>
        </a>
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="gender" />,
      cell: (row) => <TextColumn text={row.gender ? t(row.gender) : "-"} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="educationalLevel" />,
      cell: (row) => (
        <TextColumn
          text={
            row.educationalLevel
              ? row.educationalLevel[i18n.language === "ar" ? "ar" : "en"]
              : ""
          }
        />
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="subscriptions" />,
      cell: (row) => <HTMLColumn html={getSubscriptions(row.subscriptions)} />,
      sortable: false,
    },
    {
      name: <ColumnHeader title="subjects" />,
      cell: (row) => (
        <>
          <Button
            disabled={row?.subjects?.length === 0}
            className="btn btn-success btn-sm"
            onClick={() => onViewSubjects(row)}
          >
            {t("purchased")}
          </Button>
          &nbsp;
          <Button
            className="btn btn-success btn-sm"
            onClick={() => onAssignSubjects(row)}
          >
            {t("manualPurchase")}
          </Button>
        </>
      ),
      sortable: false,
      width: "260px",
    },
    {
      name: <ColumnHeader title="action" />,
      sortable: false,
      width: "120px",
      cell: (row) =>
        row.isDeleted === false ? (
          <ActionButton
            onAction={() => onToggleBlock(row)}
            state={row.isBlocked}
            actionType={ActionTypeEnum.BLOCK}
          />
        ) : (
          <span className="text-danger">{t("userDeleted")}</span>
        ),
    },
  ];

  const getSortByColumnName = (name: string) => {
    let sortBy;
    switch (name) {
      case "name.en":
      case "name.ar":
        sortBy = 1;
        break;
      default:
        sortBy = 2;
        break;
    }
    return sortBy;
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(
    getSortByColumnName("created_at:DESC")
  );
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);
  const [defaultPage, setDefaultPage] = useState(1);

  //#region effects

  useEffect(() => {
    if (!toggleBlockUserLoading && toggleBlockUserSuccess) {
      setSelectedStudent(null);
      setShowBlockUnblockModal(false);
      dispatch(getStudents(paginateQuery, history));
    }
  }, [toggleBlockUserLoading]);

  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    let filters: any = {};
    if (queryParams.has("filter[governorate]")) {
      filters = {
        ...filters,
        ["governorate"]: queryParams.get("filter[governorate]") || "",
      };
      setFilterGovernorateId(filters["governorate"] as string);
    }
    if (queryParams.has("filter[city]")) {
      filters = {
        ...filters,
        ["city"]: queryParams.get("filter[city]") || "",
      };
      setFilterCityId(filters["city"] as string);
    }
    if (queryParams.has("filter[school]")) {
      filters = {
        ...filters,
        ["school"]: queryParams.get("filter[school]") || "",
      };
      setFilterSchoolId(filters["school"] as string);
    }
    if (queryParams.has("filter[course]")) {
      filters = {
        ...filters,
        ["course"]: queryParams.get("filter[course]") || "",
      };
      setFilterCourseId(filters["course"] as string);
    }
    if (queryParams.has("filter[grade]")) {
      filters = {
        ...filters,
        ["grade"]: queryParams.get("filter[grade]") || "",
      };
      setFilterGradeId(filters["grade"] as string);
    }
    if (queryParams.has("filter[gender]")) {
      filters = {
        ...filters,
        ["gender"]: queryParams.get("filter[gender]") || "",
      };
      setFilterGenderId(filters["gender"] as string);
    }
    let query: PaginateQuery = {
      limit: queryParams.has("limit")
        ? Number(queryParams.get("limit"))
        : paginateQuery.limit,
      page: queryParams.has("page")
        ? Number(queryParams.get("page"))
        : paginateQuery.page,
      sortBy: queryParams.has("sortBy")
        ? (queryParams.get("sortBy") as string)
        : paginateQuery.sortBy,
      search: queryParams.has("search")
        ? (queryParams.get("search") as string)
        : paginateQuery.search,
      filter: Object.keys(filters).length > 0 ? filters : paginateQuery.filter,
    };
    if (
      queryParams.get("sortBy") &&
      queryParams.get("sortBy")?.split(":").length &&
      queryParams.get("sortBy")?.split(":").length == 2
    ) {
      setDefaultSortAsc(
        (queryParams.get("sortBy")?.split(":")[1] as string) === "ASC"
      );
      setDefaultSortFieldId(
        getSortByColumnName(queryParams.get("sortBy")?.split(":")[0] as string)
      );
    } else {
      setDefaultSortAsc(false);
      setDefaultSortFieldId(2);
    }
    setDefaultPage(query.page);
    setPaginateQuery(query);
  }, []);

  useEffect(() => {
    if (paginateQuery !== initState) {
      const { limit, page, search, sortBy, filter } = paginateQuery;
      let query;
      if (page) query = `?page=${page}`;
      if (limit) query += `&limit=${limit}`;
      if (search && search !== "") {
        history.replace({
          search: `&search=${search}`,
        });
      }
      if (sortBy) query += `&sortBy=${sortBy}`;
      if (search) query += `&search=${search}`;
      if (
        filter &&
        Object.keys(filter).length &&
        Object.keys(filter).length > 0
      ) {
        let filters: string = "";
        Object.keys(filter).map((key: string) => {
          if (filter[key]) filters += `&filter[${key}]=${filter[key]}`;
        });
        query += filters;
      }
      history.replace({ search: query });
      dispatch(getStudents(paginateQuery, history));
    }
  }, [paginateQuery]);

  useEffect(() => {
    dispatch(resetCitySelectList());
    dispatch(resetSchoolSelectList());
    dispatch(resetGradeSelectListByCourseLocal());
    dispatch(getCourseAndGradesSelectList());
  }, []);

  //#endregion

  //#region handlers

  const onViewSubjects = (_row: StudentDto) => {
    setSelectedStudent(_row);
    setShowSubjectModal(true);
  };

  const onAssignSubjects = (_row: StudentDto) => {
    setSelectedStudent(_row);
    setShowAssignSubjectManuallyModal(true);
  };

  const onToggleBlock = (_row: StudentDto) => {
    setSelectedStudent(_row);
    setShowBlockUnblockModal(true);
    dispatch(toggleBlockUserConsent(selectedStudent?.id as string));
  };

  const onBlockUnblockConfirm = () => {
    dispatch(
      toggleBlockUser(
        selectedStudent?.id as string,
        !(selectedStudent?.isBlocked as boolean),
        history
      )
    );
    setSelectedStudent(null);
  };

  const onSearch = (searchText: string) => {
    setPaginateQuery({ ...paginateQuery, search: searchText });
  };

  const getSortByColumnId = (columnId: number, sortDirection: string) => {
    let sortBy;
    switch (columnId) {
      case 1:
        sortBy = "name" + "." + i18n.language;
        break;
      default:
        sortBy = "created_at";
        break;
    }
    return `${sortBy}:${sortDirection.toUpperCase()}`;
  };

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page: page });
  };

  const onChangeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number
  ) => {
    setPaginateQuery({
      ...paginateQuery,
      limit: currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (column: TableColumn<StudentDto>, sortDirection: string) => {
    const sortBy = getSortByColumnId(column.id as number, sortDirection);
    setPaginateQuery({
      ...paginateQuery,
      sortBy: sortBy as string,
    });
  };

  const onChangeGovernorateFilter = (value: string) => {
    setFilterSchoolId("");
    dispatch(resetSchoolSelectList());
    setFilterCityId("");
    dispatch(resetCitySelectList());
    setFilterGovernorateId(value);
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["governorate"]: value,
        ["city"]: "",
        ["school"]: "",
      },
    });
  };

  const onChangeCityFilter = (value: string) => {
    setFilterSchoolId("");
    dispatch(resetSchoolSelectList());
    setFilterCityId(value);
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["city"]: value,
        ["school"]: "",
      },
    });
  };

  const onChangeSchoolFilter = (value: string) => {
    setFilterSchoolId(value);
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["school"]: value,
      },
    });
  };

  const onChangeCourseFilter = (value: string) => {
    setFilterGradeId("");
    dispatch(resetGradeSelectListByCourseLocal());
    setFilterCourseId(value);
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["course"]: value,
        ["grade"]: "",
      },
    });
  };

  const onChangeGradeFilter = (value: string) => {
    setFilterGradeId(value);
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["grade"]: value,
      },
    });
  };

  const onChangeGenderFilter = (value: string) => {
    setFilterGenderId(value);
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["gender"]: value,
      },
    });
  };

  const onClearFilters = () => {
    setFilterGovernorateId("");
    setFilterCityId("");
    dispatch(resetCitySelectList());
    setFilterSchoolId("");
    dispatch(resetSchoolSelectList());
    setFilterCourseId("");
    setFilterGradeId("");
    dispatch(resetGradeSelectListByCourseLocal());
    setFilterGenderId("");
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["governorate"]: "",
        ["city"]: "",
        ["school"]: "",
        ["course"]: "",
        ["grade"]: "",
        ["gender"]: "",
      },
    });
  };

  const handleSelectionRows = ({
    selectedRows,
  }: {
    selectedRows: StudentDto[];
  }) => {
    setSelectedRows(selectedRows);
  };

  const handleClearRows = () => {
    setToggleClearRows(!toggledClearRows);
    setSelectedRows([]);
  };

  const onSendPushNotification = () => {
    if (selectedRows.length > 0) {
      setShowSendPushNotificationModal(true);
    }
  };
  //#endregion

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("Students")}
            pageTitle={t("appUsers")}
            component={
              <BreadCrumbSelect
                options={[
                  {
                    title: "students",
                    url: "/users/app-users/students",
                    selected: true,
                  },
                  {
                    title: "students2",
                    url: "/users/app-users/students-v2",
                    selected: false,
                  },
                ]}
              />
            }
          />
          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <Row className="g-2 mb-3">
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <DebounceInput
                          className="form-control"
                          placeholder={`${t("searchPlaceholder")}...`}
                          minLength={2}
                          debounceTimeout={300}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => onSearch(event.target.value)}
                          value={paginateQuery.search}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <GovernorateSelect
                          value={filterGovernorateId}
                          placeholder={t("filterByAddressGovernorate")}
                          type="select"
                          id="governorateId"
                          name="governorateId"
                          className="form-select"
                          aria-label={t("filterByAddressGovernorate")}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onChangeGovernorateFilter(event.target.value)
                          }
                        ></GovernorateSelect>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <CitySelect
                          value={filterCityId}
                          placeholder={t("filterByAddressCity")}
                          type="select"
                          id="cityId"
                          name="cityId"
                          className="form-select"
                          aria-label={t("filterByAddressCity")}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onChangeCityFilter(event.target.value)
                          }
                          governorateId={filterGovernorateId}
                        ></CitySelect>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <SchoolSelect
                          value={filterSchoolId}
                          placeholder={t("filterBySchool")}
                          type="select"
                          id="schoolId"
                          name="schoolId"
                          className="form-select"
                          aria-label={t("filterBySchool")}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onChangeSchoolFilter(event.target.value)
                          }
                          governorateId={filterGovernorateId}
                          cityId={filterCityId}
                        ></SchoolSelect>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <CourseSelect
                          value={filterCourseId}
                          placeholder={t("filterByCourse")}
                          type="select"
                          id="courseId"
                          name="courseId"
                          className="form-select"
                          aria-label={t("filterByCourse")}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onChangeCourseFilter(event.target.value)
                          }
                        ></CourseSelect>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <GradeSelect
                          courseId={filterCourseId}
                          value={filterGradeId}
                          placeholder={t("filterByGrade")}
                          type="select"
                          id="gradeId"
                          name="gradeId"
                          className="form-select"
                          aria-label={t("filterByGrade")}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onChangeGradeFilter(event.target.value)
                          }
                        ></GradeSelect>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <select
                          value={filterGenderId}
                          className="form-select form-select-md"
                          onChange={(event: any) =>
                            onChangeGenderFilter(event.target.value)
                          }
                        >
                          <option value="">{t("filterByGender")}</option>
                          <option value="Female">{t("female")}</option>
                          <option value="Male">{t("male")}</option>
                        </select>
                      </div>
                    </Col>
                    <Col className="col-sm-auto ms-auto">
                      <div className="list-grid-nav hstack gap-1">
                        <Button
                          color="success"
                          onClick={() => onClearFilters()}
                        >
                          {t("clearFilters")}
                        </Button>
                        <Button
                          color="success"
                          onClick={() => setPaginateQuery({ ...paginateQuery })}
                        >
                          {t("refresh")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <Row className="g-2 mb-3">
                    <Col className="col-sm-auto ms-auto">
                      <div className="list-grid-nav hstack gap-1">
                        <Button
                          disabled={selectedRows.length === 0}
                          color="success"
                          onClick={onSendPushNotification}
                        >
                          <i className="ri-notification-line me-2 align-bottom"></i>
                          {t("sendNotification")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12}>
                      <AppTable
                        selectableRows
                        onSelectedRowsChange={handleSelectionRows}
                        clearSelectedRows={toggledClearRows}
                        theme={"dark"}
                        columns={columns}
                        data={response?.data || []}
                        progressPending={loading}
                        progressComponent={<TableSpinner />}
                        pagination
                        paginationServer
                        sortServer
                        onSort={onSort}
                        onChangeRowsPerPage={onChangeRowsPerPage}
                        onChangePage={onChangePage}
                        defaultSortFieldId={defaultSortFieldId}
                        paginationDefaultPage={defaultPage}
                        defaultSortAsc={defaultSortAsc}
                        persistTableHead
                        paginationTotalRows={response?.meta?.totalItems}
                        paginationComponentOptions={{
                          rowsPerPageText: t("rowsPerPage"),
                          rangeSeparatorText: t("of"),
                        }}
                        fixedHeader={true}
                        fixedHeaderScrollHeight="60vh"
                      />
                    </Col>
                  </Row>
                  <ConfirmationModal
                    confirmationButtonInProgressTitle={t("saving")}
                    confirmationButtonTitle={
                      (selectedStudent?.isBlocked as boolean)
                        ? t("unblock")
                        : t("block")
                    }
                    description={
                      (selectedStudent?.isBlocked as boolean)
                        ? t("unblockConfirmationMessage", {
                            username:
                              i18n.language === "ar"
                                ? selectedStudent?.name.ar
                                : selectedStudent?.name.en,
                          })
                        : t("blockConfirmationMessage", {
                            username:
                              i18n.language === "ar"
                                ? selectedStudent?.name.ar
                                : selectedStudent?.name.en,
                          })
                    }
                    icon={<></>}
                    title={t("areYouSure")}
                    loading={toggleBlockUserLoading}
                    show={showBlockUnblockModal}
                    onConfirmClick={() => onBlockUnblockConfirm()}
                    onCloseClick={() => {
                      setShowBlockUnblockModal(false);
                    }}
                    error={toggleBlockUserError}
                  />
                  {selectedStudent &&
                    selectedStudent.subjects &&
                    selectedStudent.subjects.length > 0 && (
                      <StudentsSubjectsModal
                        subjects={selectedStudent?.subjects}
                        show={showSubjectsModal}
                        onCloseClick={() => {
                          setShowSubjectModal(false);
                        }}
                      />
                    )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      {selectedRows && selectedRows.length > 0 && (
        <SendPushNotificationModal
          show={showSendPushNotificationModal}
          onCloseClick={() => {
            setShowSendPushNotificationModal(false);
            handleClearRows();
          }}
          mode="user"
          user={
            selectedRows.map((user) => {
              return { id: user.id, name: user.name };
            }) as any
          }
        />
      )}
      {selectedStudent && (
        <ManualPurchaseSubjectModal
          userId={selectedStudent?.id}
          name={selectedStudent?.name}
          phoneNumber={selectedStudent?.phoneNumber}
          show={showAssignSubjectManuallyModal}
          onCloseClick={() => {
            setShowAssignSubjectManuallyModal(false);
            dispatch(getStudents(paginateQuery, history));
          }}
        />
      )}
    </React.Fragment>
  );
};
